import { useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { projectMembersMapAtom } from '~/components/Map/Member/MemberStore';
import { ProfileAPIs } from '~/common/typescript/apis/ProfileAPIs';
import type { User_Base } from '~/common/typescript/models/User';

// In-memory cache for fallback user profile lookups
const globalUserCache = new Map<number, User_Base>();
const pendingUserRequests = new Map<number, Promise<User_Base | null>>();

const fetchUserProfileCached = async (userId: number): Promise<User_Base | null> => {
  if (globalUserCache.has(userId)) {
    return globalUserCache.get(userId)!;
  }

  if (pendingUserRequests.has(userId)) {
    return pendingUserRequests.get(userId)!;
  }

  const promise = ProfileAPIs.remote_getProfile(userId)
    .then((userData) => {
      if (userData) {
        globalUserCache.set(userId, userData);
      }
      return userData || null;
    })
    .catch((err) => {
      console.warn(`[useUserById] Failed to fetch user profile for ID ${userId}:`, err);
      return null;
    })
    .finally(() => {
      pendingUserRequests.delete(userId);
    });

  pendingUserRequests.set(userId, promise);
  return promise;
};

/**
 * Fetch a single user by numeric ID.
 * - Prioritizes looking up from preloaded project members (projectMembersMapAtom) with 0ms latency.
 * - Falls back to ProfileAPIs.remote_getProfile (GET /api/users-ext/:id) with in-memory caching and request deduplication.
 */
export const useUserById = (userId?: number) => {
  const projectMembersMap = useAtomValue(projectMembersMapAtom);

  const memberUser = userId ? projectMembersMap[userId] : undefined;
  const cachedUser = userId ? globalUserCache.get(userId) : undefined;

  const [user, setUser] = useState<User_Base | null>(memberUser || cachedUser || null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!userId) {
      setUser(null);
      setLoading(false);
      return;
    }

    // 1. Found in project members map
    if (projectMembersMap[userId]) {
      setUser(projectMembersMap[userId]);
      setLoading(false);
      return;
    }

    // 2. Found in global cache
    if (globalUserCache.has(userId)) {
      setUser(globalUserCache.get(userId)!);
      setLoading(false);
      return;
    }

    // 3. Fetch from remote ProfileAPIs (GET /api/users-ext/:id)
    let isCancelled = false;
    setLoading(true);

    fetchUserProfileCached(userId)
      .then((userData) => {
        if (!isCancelled) {
          setUser(userData);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [userId, projectMembersMap]);

  return { user: memberUser || user, loading };
};
